import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body: string;
  cover_image_url: string | null;
  published_at: string | null;
};

export default function UpdateDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!slug) return;
      setError(null);
      setLoading(true);

      const { data, error } = await supabase
        .from('posts')
        .select('id, slug, title, excerpt, body, cover_image_url, published_at')
        .eq('slug', slug)
        .eq('is_published', true)
        .single();

      if (!isMounted) return;

      if (error) {
        console.error('Failed to load post', error);
        setError('This update could not be found.');
        setPost(null);
      } else if (data) {
        setPost(data as Post);
      }

      setLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  return (
    <div className="container" style={{ padding: '1.25rem 1rem 2rem 1rem' }}>
      <header style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <Link to="/updates" style={{ color: 'var(--color-accent)', textDecoration: 'none', fontWeight: 600 }}>
          ← Updates
        </Link>
      </header>

      {loading && (
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0 }}>Loading…</p>
        </div>
      )}

      {error && !loading && (
        <div className="card" style={{ padding: '1.5rem', border: '1px solid var(--color-danger)' }}>
          <p style={{ color: 'var(--color-danger)', margin: 0 }}>{error}</p>
        </div>
      )}

      {!loading && !error && post && (
        <article className="card" style={{ padding: '1.5rem', border: '1px solid #1f1f1f' }}>
          <header style={{ marginBottom: '1rem' }}>
            <p style={{ color: '#999', margin: '0 0 0.35rem', fontSize: '0.9rem' }}>
              {post.published_at ? new Date(post.published_at).toLocaleDateString() : ''}
            </p>
            <h1 style={{ margin: 0, fontSize: '1.6rem', lineHeight: 1.3 }}>{post.title}</h1>
            {post.excerpt && (
              <p style={{ color: '#cfcfcf', marginTop: '0.5rem', lineHeight: 1.45 }}>{post.excerpt}</p>
            )}
          </header>

          {post.cover_image_url && (
            <img
              src={post.cover_image_url}
              alt=""
              style={{
                width: '100%',
                maxHeight: '340px',
                objectFit: 'cover',
                borderRadius: '12px',
                marginBottom: '1rem',
              }}
              loading="lazy"
            />
          )}

          <div
            style={{
              color: '#e4e4e4',
              lineHeight: 1.6,
              fontSize: '1rem',
              whiteSpace: 'pre-line',
            }}
          >
            {post.body}
          </div>
        </article>
      )}
    </div>
  );
}
